import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Mail, Phone, MapPin, MessageSquare, Send, CheckCircle, Building2, Shield, UserCheck } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const SUPPORT_EMAIL = "contact@misyou.in";
const SUPPORT_PHONE = "+91 8146499739";
const SUPPORT_PHONE_TEL = "+918146499739";
const SUPPORT_PHONE_WHATSAPP = "918146499739";

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const buildMailto = () => {
    const subject = encodeURIComponent(form.subject || 'MISYOU support enquiry');
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open user's mail client pre-filled. Email backend integration is on the roadmap.
    window.location.href = buildMailto();
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">Contact MISYOU</h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            We're here to help — whether you're reporting a case, need account support, or want to give feedback.
          </p>
        </div>

        {/* Business Information — public disclosure for payment-gateway
            compliance (PayU / Razorpay / RBI Payment Aggregator rules).
            Covers who we are, what we do, who to contact for grievances,
            and how we handle payment security. */}
        <div className="mb-10 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="business-info-block">
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-primary" />
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Who we are</p>
              </div>
              <p className="text-sm font-semibold text-slate-900">MISYOU</p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                A subscription-based Software-as-a-Service (SaaS) platform helping families and communities
                locate missing loved ones. <strong>B2C</strong> only &middot; India-only &middot; INR &middot; 100% digital service.
              </p>
              <p className="text-[11px] text-slate-500 mt-2">
                Legal entity: <strong>Individually owned by Dinesh Bansal</strong>
              </p>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-4 h-4 text-emerald-600" />
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Grievance Officer (IT Rules 2021)</p>
              </div>
              <p className="text-sm font-semibold text-slate-900">Mr. Naresh Kumar</p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                <a href="mailto:grievance@misyou.in" className="text-primary hover:underline">grievance@misyou.in</a>
                <br />
                <a href="tel:+919041299739" className="text-primary hover:underline">+91 90412 99739</a>
              </p>
              <p className="text-[11px] text-slate-500 mt-2">
                Response SLA: 24 hours for CSAM / child-safety &middot; 72 hours for other complaints.
              </p>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-blue-600" />
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Payment Security</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Card details never touch our servers &mdash; handled entirely by PCI-DSS-compliant gateways
                (Paytm Payments Services). No card storage. No auto-renewal. INR only.
              </p>
              <p className="text-[11px] text-slate-500 mt-2">
                See <a href="/refund" className="underline hover:text-primary">Refund Policy</a> &middot;{' '}
                <a href="/privacy" className="underline hover:text-primary">Privacy</a> &middot;{' '}
                <a href="/terms" className="underline hover:text-primary">Terms</a> &middot;{' '}
                <a href="/shipping" className="underline hover:text-primary">Shipping &amp; Delivery</a>
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Contact methods */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="block"
              data-testid="contact-email-link"
            >
              <Card className="hover:shadow-lg transition-shadow border-slate-100">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">Email support</p>
                    <p className="font-semibold text-slate-900">{SUPPORT_EMAIL}</p>
                    <p className="text-xs text-slate-500 mt-1">Replies within 24 hours (Mon–Sat)</p>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a
              href={`tel:${SUPPORT_PHONE_TEL}`}
              className="block"
              data-testid="contact-phone-link"
            >
              <Card className="hover:shadow-lg transition-shadow border-slate-100">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">Customer care</p>
                    <p className="font-semibold text-slate-900">{SUPPORT_PHONE}</p>
                    <p className="text-xs text-slate-500 mt-1">India · Mon–Sat · 10am–7pm IST</p>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a
              href={`https://wa.me/${SUPPORT_PHONE_WHATSAPP}?text=${encodeURIComponent('Hi MISYOU, I need help with...')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="contact-whatsapp-link"
            >
              <Card className="hover:shadow-lg transition-shadow border-slate-100">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.5 14.4c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2l-1 1.3c-.2.2-.4.3-.7.1-1.3-.7-2.4-1.5-3.4-3-.3-.4.3-.4.8-1.3.1-.2 0-.4 0-.5-.1-.2-.7-1.7-1-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.4-1.2 1.1-1.2 2.7s1.2 3.1 1.4 3.3c.2.2 2.4 3.7 5.8 5.1 2.7 1.1 3.2 1 3.7.9.5-.1 1.9-.8 2.2-1.5.3-.7.3-1.4.2-1.5-.1-.2-.3-.3-.7-.5zM12 0C5.4 0 0 5.4 0 12c0 2.1.5 4 1.5 5.8L0 24l6.4-1.6c1.7.9 3.6 1.5 5.6 1.5 6.6 0 12-5.4 12-12S18.6 0 12 0zm0 22c-1.9 0-3.7-.5-5.3-1.4l-.4-.2-3.8.9.9-3.6-.2-.4C2.1 15.7 2 13.9 2 12c0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10z" /></svg>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">WhatsApp</p>
                    <p className="font-semibold text-slate-900">{SUPPORT_PHONE}</p>
                    <p className="text-xs text-slate-500 mt-1">Quick chat — typical reply within 2 hours</p>
                  </div>
                </CardContent>
              </Card>
            </a>

            <Card className="border-slate-100">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Registered office</p>
                  <p className="font-semibold text-slate-900">MISYOU</p>
                  <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                    Individually owned by Dinesh Bansal
                  </p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    St. No.-05, Natt Road,<br />
                    SBS Nagar, Talwandi Sabo,<br />
                    Bathinda, Punjab &ndash; 151302, India
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg border-slate-100">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-1">Send us a message</h2>
                <p className="text-sm text-slate-500 mb-6">
                  Fill this in and we'll route it to your mail app so you can send it from your own email.
                </p>

                {sent ? (
                  <div className="text-center py-10" data-testid="contact-form-sent">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <h3 className="font-semibold text-slate-900">Your message is on its way</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      We've opened your mail client. If nothing happened, email us directly at{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">{SUPPORT_EMAIL}</a>.
                    </p>
                    <Button variant="outline" className="mt-5" onClick={() => setSent(false)}>
                      Send another message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Your name</Label>
                        <Input
                          id="name"
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Rajinder Singh"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        required
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        placeholder="Need help with my case"
                        data-testid="contact-subject-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        required
                        rows={6}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us what's going on..."
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button type="submit" size="lg" className="w-full" data-testid="contact-submit-btn">
                      <Send className="w-4 h-4 mr-2" />
                      Send message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
